import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Eye, EyeOff } from "lucide-react";
import {
  listMyPortfolio,
  upsertPortfolioItem,
  deletePortfolioItem,
} from "@/lib/api/student-portal.functions";
import { PORTFOLIO_KINDS } from "@/lib/student-portal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/portal/student/portfolio")({
  component: PortfolioPage,
});

function PortfolioPage() {
  const list = useServerFn(listMyPortfolio);
  const upsert = useServerFn(upsertPortfolioItem);
  const remove = useServerFn(deletePortfolioItem);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["student-portfolio"], queryFn: () => list() });

  const [form, setForm] = useState({
    kind: "assignment",
    title: "",
    description: "",
    link_url: "",
  });
  const [saving, setSaving] = useState(false);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await upsert({ data: { ...form, visible: true } });
      toast.success("Added to your portfolio");
      setForm({ kind: "assignment", title: "", description: "", link_url: "" });
      qc.invalidateQueries({ queryKey: ["student-portfolio"] });
    } catch (err: any) {
      toast.error("Could not add", { description: err?.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
      <section className="space-y-3">
        {q.isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-navy" />
        ) : (q.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">
            Your portfolio is empty. Add your best work, certificates and achievements — this is what
            recruiters see.
          </p>
        ) : (
          q.data!.items.map((i: any) => (
            <article key={i.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-widest text-gold">{i.kind}</p>
                  <h3 className="font-display text-lg text-navy">{i.title}</h3>
                  {i.description && (
                    <p className="mt-1 text-sm text-foreground/85">{i.description}</p>
                  )}
                  {i.link_url && (
                    <a
                      href={i.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-block text-sm text-navy hover:underline"
                    >
                      View item →
                    </a>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    aria-label={i.visible ? "Hide from recruiters" : "Show to recruiters"}
                    onClick={async () => {
                      await upsert({ data: { ...i, visible: !i.visible } });
                      qc.invalidateQueries({ queryKey: ["student-portfolio"] });
                    }}
                    className="rounded-md border border-input p-1.5 hover:border-navy"
                  >
                    {i.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    aria-label="Delete"
                    onClick={async () => {
                      await remove({ data: { id: i.id } });
                      qc.invalidateQueries({ queryKey: ["student-portfolio"] });
                    }}
                    className="rounded-md border border-input p-1.5 text-destructive hover:border-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </article>
          ))
        )}
      </section>

      <form onSubmit={add} className="h-fit space-y-3 rounded-lg border border-border bg-card p-5">
        <h3 className="font-display text-lg text-navy">Add portfolio item</h3>
        <div>
          <Label htmlFor="pf_kind">Type</Label>
          <select
            id="pf_kind"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            {PORTFOLIO_KINDS.map((k) => (
              <option key={k} value={k}>
                {k.replace(/-/g, " ")}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="pf_title">Title</Label>
          <Input
            id="pf_title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />
        </div>
        <div>
          <Label htmlFor="pf_desc">Description</Label>
          <Textarea
            id="pf_desc"
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="pf_link">Link (optional)</Label>
          <Input
            id="pf_link"
            value={form.link_url}
            onChange={(e) => setForm({ ...form, link_url: e.target.value })}
          />
        </div>
        <Button type="submit" disabled={saving} className="w-full bg-navy hover:bg-navy/90">
          <Plus className="mr-2 h-4 w-4" /> {saving ? "Adding…" : "Add item"}
        </Button>
      </form>
    </div>
  );
}
